import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, LockKeyhole, ShipWheel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/auth-context";

export function LoginPage() {
  const { user, signIn, isLoading, authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isLoading && user) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? "/app/dashboard"} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const result = await signIn(email.trim(), password);
    setIsSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    navigate("/app/dashboard", { replace: true });
  }

  return (
    <main className="grid min-h-screen bg-slate-950 lg:grid-cols-[1.1fr_0.9fr]">
      <section className="relative hidden overflow-hidden px-12 py-14 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.22),transparent_34%),radial-gradient(circle_at_80%_80%,rgba(14,165,233,0.14),transparent_28%)]" />
        <div className="relative flex items-center gap-3 text-sm font-semibold tracking-wide">
          <span className="grid size-9 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15">
            <ShipWheel className="size-5" />
          </span>
          SHIPMENT INTELLIGENCE
        </div>
        <div className="relative max-w-xl">
          <p className="mb-5 text-sm font-semibold uppercase tracking-[0.25em] text-sky-300">Operations control</p>
          <h1 className="text-5xl font-semibold leading-tight tracking-tight">A clearer view of every shipment.</h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-slate-300">
            Bring your logistics team, customers, and operational decisions into one calm workspace.
          </p>
        </div>
        <p className="relative text-sm text-slate-500">Secure tenant-isolated workspace</p>
      </section>

      <section className="flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <div className="flex items-center gap-3 text-sm font-semibold tracking-wide text-slate-900">
              <span className="grid size-9 place-items-center rounded-xl bg-slate-900 text-white">
                <ShipWheel className="size-5" />
              </span>
              SHIPMENT INTELLIGENCE
            </div>
          </div>
          <div className="mb-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">
              <LockKeyhole className="size-3.5" />
              Secure workspace sign-in
            </div>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Welcome back</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">Sign in to continue to your operations workspace.</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
            </div>

            {(error || authError) && (
              <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                {error ?? authError}
              </div>
            )}

            <Button className="w-full" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? "Signing in…" : "Sign in"}
              {!isSubmitting && <ArrowRight className="size-4" />}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs leading-5 text-slate-400">Access is managed by your organization administrator.</p>
        </div>
      </section>
    </main>
  );
}
